import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import { ListingDataSuggestionApiClient } from 'api/listingDataSuggestion';
import { ProcessStatus } from 'models/types';
import { PaginationModel, ListingDataSuggestionModel } from 'models';
import { JSONObject } from 'models/Api/types';

export function useDetailedStatusListingDataSuggestions(status: ProcessStatus, page: number = 1, perPage: number = 10) {
  return useQuery<PaginationModel<ListingDataSuggestionModel>>({
    queryKey: ['listing-data-suggestions-detailed-status', status, page, perPage],
    queryFn: () => ListingDataSuggestionApiClient.getDetailedStatusSuggestions(status, page, perPage),
    enabled: !!status
  });
}

export function useCreateListingDataSuggestion() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (payload: { listingId: string; data: JSONObject }) =>
      ListingDataSuggestionApiClient.createListingDataSuggestion(payload.listingId, payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listing-data-suggestions-detailed-status'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-pending-listings'] });
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      toast({
        title: 'Listing data suggestion created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create listing data suggestion',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
}

export function useApproveListingDataSuggestion() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (payload: { listing_data_suggestion_id: string }) =>
      ListingDataSuggestionApiClient.approveSuggestion(payload),
    onSuccess: () => {
      // Invalidate listing data suggestions queries
      queryClient.invalidateQueries({ queryKey: ['listing-data-suggestions-detailed-status'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-pending-listings'] });
      toast({
        title: 'Listing data suggestion approved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to approve listing data suggestion',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
}

export function useRejectListingDataSuggestion() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (payload: { listing_data_suggestion_id: string }) =>
      ListingDataSuggestionApiClient.rejectSuggestion(payload),
    onSuccess: () => {
      // Invalidate listing data suggestions queries
      queryClient.invalidateQueries({ queryKey: ['listing-data-suggestions-detailed-status'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-pending-listings'] });
      toast({
        title: 'Listing data suggestion rejected successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to reject listing data suggestion',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
}

// Bulk operations - approve/reject multiple suggestions at once
export function useBulkApproveListingDataSuggestions() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (suggestionIds: string[]): Promise<{ succeeded: number; failed: number }> => {
      const results = await Promise.allSettled(
        suggestionIds.map(id =>
          ListingDataSuggestionApiClient.approveSuggestion({ listing_data_suggestion_id: id })
        )
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      return { succeeded, failed };
    },
    onSuccess: ({ succeeded, failed }) => {
      queryClient.invalidateQueries({ queryKey: ['listing-data-suggestions-detailed-status'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-pending-listings'] });
      queryClient.invalidateQueries({ queryKey: ['listings'] });

      toast({
        title: 'Bulk approve completed',
        description: failed > 0
          ? `${succeeded} succeeded, ${failed} failed`
          : `${succeeded} suggestion${succeeded !== 1 ? 's' : ''} approved`,
        status: failed > 0 ? 'warning' : 'success',
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Bulk approve failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  });
}

export function useBulkRejectListingDataSuggestions() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (suggestionIds: string[]): Promise<{ succeeded: number; failed: number }> => {
      const results = await Promise.allSettled(
        suggestionIds.map(id =>
          ListingDataSuggestionApiClient.rejectSuggestion({ listing_data_suggestion_id: id })
        )
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      return { succeeded, failed };
    },
    onSuccess: ({ succeeded, failed }) => {
      queryClient.invalidateQueries({ queryKey: ['listing-data-suggestions-detailed-status'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-pending-listings'] });
      queryClient.invalidateQueries({ queryKey: ['listings'] });

      toast({
        title: 'Bulk reject completed',
        description: failed > 0
          ? `${succeeded} succeeded, ${failed} failed`
          : `${succeeded} suggestion${succeeded !== 1 ? 's' : ''} rejected`,
        status: failed > 0 ? 'warning' : 'success',
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Bulk reject failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  });
}
